import React, { createContext, useContext, useCallback, useReducer } from 'react';
import type { Game } from '@aco/shared';

interface GameState {
  game: Game | null;
  loading: boolean;
  error: string | null;
}

type GameAction =
  | { type: 'SET_GAME'; payload: Game }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR' };

function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'SET_GAME':
      return { ...state, game: action.payload, loading: false, error: null };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR':
      return { game: null, loading: false, error: null };
    default:
      return state;
  }
}

interface GameContextValue {
  state: GameState;
  setGame: (game: Game) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  clearGame: () => void;
}

const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(gameReducer, {
    game: null,
    loading: false,
    error: null,
  });

  const setGame = useCallback((game: Game) => dispatch({ type: 'SET_GAME', payload: game }), []);
  const setLoading = useCallback((loading: boolean) => dispatch({ type: 'SET_LOADING', payload: loading }), []);
  const setError = useCallback((error: string) => dispatch({ type: 'SET_ERROR', payload: error }), []);
  const clearGame = useCallback(() => dispatch({ type: 'CLEAR' }), []);

  return (
    <GameContext.Provider value={{ state, setGame, setLoading, setError, clearGame }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGameContext(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGameContext must be used within a GameProvider');
  return ctx;
}
